/**
 * English catalogue.
 *
 * Typed against `es.ts`: if a key is missing or misspelt here, TypeScript
 * fails to compile. That check is the whole reason the catalogues are flat —
 * a translation that silently falls back to a blank string is worse than one
 * that never ships.
 *
 * Tone follows the Spanish: plain, direct, and never promising something the
 * device cannot do.
 */

import type { es } from './es.js';

export const en: Record<keyof typeof es, string> = {
    // --- Common --------------------------------------------------------
    'comun.atras': 'Back',
    'comun.cerrar': 'Close',
    'comun.reintentar': 'Try again',
    'comun.aceptar': 'OK',
    'comun.cargando': 'Loading…',
    'comun.de': 'of',
    'comun.sin_conexion': 'Offline · everything saved on your phone',

    // --- 00 · Device enrolment -----------------------------------------
    'alta.etiqueta': 'FIRST RUN · ONE TIME ONLY',
    'alta.titulo': 'Link your phone',
    'alta.intro':
        'This link works only once. Once linked, your phone gets its own credential and you will not need the link again.',
    'alta.paso_invitacion': 'Invitation verified',
    'alta.paso_invitacion_sub': 'Valid link for {empleada}',
    'alta.paso_pin': 'Create your 6-digit PIN',
    'alta.paso_pin_sub': 'You will use it every day to start your shift',
    'alta.paso_instalar': 'Add the app to your home screen',
    'alta.paso_instalar_ios':
        'Share → “Add to Home Screen”. From then on you open the icon and enter only your PIN.',
    'alta.paso_instalar_android':
        'Menu → “Install app”. From then on you open the icon and enter only your PIN.',
    'alta.paso_instalar_otro':
        'From then on you come back to this address with your PIN. The invitation link is no longer needed.',
    'alta.dev_movil': 'phone',
    'alta.dev_video': 'video',
    'alta.dev_sin_video': 'not supported',
    'alta.dev_segundo_plano': 'background upload',
    'alta.dev_si': 'yes',
    'alta.dev_no': 'no',
    'alta.dispositivo': 'This device',
    'alta.nota_revocacion':
        'The office can revoke this phone at any time. If you lose it, tell them the same day.',
    'alta.boton': 'Link and create PIN',
    'alta.sin_red': 'No connection',
    'alta.sin_red_ayuda':
        'Signing up needs a connection: this link is checked on the server. Join wifi or mobile data and try again. Once you are set up, the app works with no coverage.',
    'alta.reintentar': 'Try again',
    'alta.error_pin_existente':
        'You already have a PIN. To add this phone, type the PIN you use, not a new one.',
    'alta.error_bloqueada': 'This link is blocked',
    'alta.error_bloqueada_ayuda':
        'Too many wrong PINs. Ask the office for another link; if you cannot remember your PIN, ask them to reset it.',
    'alta.sin_token': 'This link is not valid',
    'alta.sin_token_ayuda':
        'Open the full link the office sent you, code included. If you typed it by hand it may have been cut short.',
    'alta.error_caducada': 'The invitation has expired',
    'alta.error_caducada_ayuda': 'Invitations last 24 hours. Ask the office for a new one.',
    'alta.error_usada': 'This invitation has already been used',
    'alta.error_usada_ayuda': 'If you changed phones, the office has to issue a new invitation.',
    'alta.paso_invitacion_sub_abierta': 'Valid, unused link',
    'alta.paso_datos': 'Enter your name and details',
    'alta.paso_datos_sub': 'Only the name is required',
    'alta.datos_titulo': 'Your details',
    'alta.datos_intro':
        'This is the name the office will see and the one that goes on every video you record. Write it as you want to be called.',
    'alta.campo_nombre': 'Your name',
    'alta.campo_nombre_ph': 'E.g. Marta G.',
    'alta.campo_telefono': 'Phone',
    'alta.campo_email': 'Email',
    'alta.campo_idioma': 'App language',
    'alta.opcional': 'optional',
    'alta.falta_nombre': 'Enter your name to continue',
    'alta.error_email': 'That email does not look valid',
    'alta.error_email_repetido': 'That email is already registered',
    'alta.continuar': 'Continue',
    'alta.hola': 'Hi, {empleada}',

    // --- PIN -----------------------------------------------------------
    'pin.crear_titulo': 'Choose your PIN',
    'pin.crear_intro': 'Six digits. Do not share it with anyone, not even the office.',
    'pin.repetir_titulo': 'Repeat the PIN',
    'pin.no_coincide': 'The two PINs do not match',
    'pin.debil_iguales': 'It cannot be the same digit six times',
    'pin.saludo': 'Welcome back',
    'pin.subtitulo': '{empleada} · {dispositivo}. Enter your 6-digit PIN to start your shift.',
    'pin.error': 'Wrong PIN',
    'pin.error_intentos': 'Wrong PIN · {intentos} attempt{intentos:|s} left',
    'pin.bloqueado': 'Contact the office to unlock',
    'pin.bloqueado_ayuda': 'This phone locked after 5 attempts. Only the office can unlock it.',
    'pin.olvidado': 'I forgot my PIN',
    'pin.olvidado_ayuda':
        'A PIN cannot be recovered. Ask the office: they reset it and you pick a new one on this same phone.',
    'pin.reiniciado_titulo': 'Choose a new PIN',
    'pin.reiniciado_intro':
        'The office reset your PIN. Choose a new six-digit one: only you will know it.',
    'pin.reiniciado_ya': 'Your PIN is still active. Enter the one you had.',
    'pin.borrar': 'Delete',

    // --- 02 · Today ----------------------------------------------------
    'hoy.saludo': 'Good morning, {nombre}',
    'hoy.saludo_tarde': 'Good afternoon, {nombre}',
    'hoy.progreso': '{hechas} of {total} done',
    'hoy.progreso_sub': '{pendientes} left today · {subiendo} uploading',
    'hoy.progreso_sub_sin_subida': '{pendientes} left today',
    'hoy.grupo_pendientes': 'TO DO',
    'hoy.grupo_completadas': 'DONE',
    'hoy.grupo_libres': 'Open',
    'hoy.grupo_libres_ayuda': 'Nobody is assigned these. Take one if you have time.',
    'hoy.coger': 'Take',
    'hoy.urgente': 'Urgent',
    'hoy.dormitorios': '{n} bed.',
    'hoy.salida': 'check-out {hora}',
    'hoy.empezar': 'Start',
    'hoy.continuar': 'Continue',
    'hoy.completada': '{hora} · video {duracion} uploaded',
    'hoy.completada_fotos': '{hora} · {fotos} photos uploaded',
    'hoy.subiendo': 'Uploading {pct}%',
    'hoy.vacio': 'You have no cleanings assigned today',
    'hoy.vacio_ayuda':
        'If you think that is wrong, tell the office. You can also scan an apartment QR code.',
    'hoy.escanear': 'Scan QR',
    'hoy.menu': 'Menu',
    'hoy.menu_cola': 'Upload queue',
    'hoy.menu_idioma': 'Language',
    'hoy.menu_salir': 'Sign out',

    // --- 03 · QR / NFC -------------------------------------------------
    'qr.titulo': 'Scan the apartment QR',
    'qr.ayuda': 'Point at the code next to the door. It opens as soon as it is recognised.',
    'qr.manual': 'Enter the code by hand',
    'qr.manual_titulo': 'Apartment code',
    'qr.manual_ayuda': 'The 32 characters printed under the QR.',
    'qr.camara_sistema': 'Use the phone camera',
    'qr.camara_sistema_ayuda':
        'Open the phone camera app and point at the code: the link opens this app on the right apartment. This browser cannot scan from inside the app.',
    'qr.escaneando': 'Looking for the code…',
    'qr.sin_permiso': 'No camera access',
    'qr.no_asignado': 'This apartment is not on your list today',
    'qr.no_asignado_ayuda':
        '{apartamento} is not yours today. If you went anyway, tell the office before you start.',

    // --- 04 · Room walkthrough -----------------------------------------
    'recorrido.subtitulo_video': 'Guided walkthrough · {n} room{n:|s}',
    'recorrido.subtitulo_fotos': 'Photos per room · {n} room{n:|s}',
    'recorrido.modo_video': 'Guided video',
    'recorrido.modo_video_sub': 'Short clip per room',
    'recorrido.modo_fotos': 'Photos',
    'recorrido.modo_fotos_sub': 'Checklist per room',
    'recorrido.siguiente_etiqueta': 'Next',
    'recorrido.grabar': 'Record {estancia}',
    'recorrido.fotografiar': 'Photos of {estancia}',
    'recorrido.terminar': 'Go to summary',
    'recorrido.no_accesible': 'Mark as not accessible',
    'recorrido.no_accesible_titulo': 'Could not get in',
    'recorrido.no_accesible_ayuda':
        'Tell us why you could not get in. It is recorded with the cleaning.',
    'recorrido.no_accesible_ejemplo': 'e.g. the guest was still inside',
    'recorrido.sin_estancias': 'This apartment has no rooms set up',
    'recorrido.sin_estancias_ayuda':
        'Without rooms there is no way to record proof. Tell the office to set them up; meanwhile you can close the visit, but it will count as not recorded.',

    // --- 05 · Recording ------------------------------------------------
    'grabar.contexto': '{estancia} · {n}/{total}',
    'grabar.guia_titulo': 'Film in this order',
    'grabar.empezar': 'Start recording',
    'grabar.parar': 'Stop',
    'grabar.repetir': 'Retake',
    'grabar.linterna': 'Torch',
    'grabar.corto': 'Too short: {segundos} seconds minimum',
    'grabar.aviso_largo': '{segundos} s left',
    'grabar.sin_camara': 'No camera access',
    'grabar.sin_camara_ios':
        'On iPhone: Settings → Safari → Camera → Allow. Then open the app again from its icon.',
    'grabar.sin_camara_android':
        'Tap the padlock in the address bar → Permissions → Camera → Allow.',
    'grabar.sin_camara_pwa':
        'Permission is asked per site. If you installed the app on your home screen, grant it again from here.',

    // --- 06 · Photo checklist ------------------------------------------
    'fotos.titulo': '{estancia} · photos',
    'fotos.instruccion': 'Take one photo per point. At least {n} to close the room.',
    'fotos.hacer': 'Take photo',
    'fotos.rehacer': 'Retake',
    'fotos.faltan': '{n} photo{n:|s} missing',
    'fotos.aviso_incidencia': 'Anything broken or stained? Add an issue and we will tell the office.',
    'fotos.anadir_incidencia': 'Add an issue',
    'fotos.siguiente_estancia': 'Next room',

    // --- Issues --------------------------------------------------------
    'incidencia.titulo': 'New issue',
    'incidencia.texto': 'What you saw (required)',
    'incidencia.texto_ejemplo': 'e.g. the shower screen is cracked',
    'incidencia.gravedad': 'Severity',
    'incidencia.gravedad_baja': 'Minor',
    'incidencia.gravedad_media': 'Normal',
    'incidencia.gravedad_alta': 'Urgent',
    'incidencia.guardar': 'Save issue',
    'incidencia.guardada': 'Issue recorded · the office will see it',

    // --- 07 · Summary --------------------------------------------------
    'resumen.titulo': 'Cleaning recorded',
    'resumen.subtitulo': '{apartamento} · {fecha}, {hora} · {duracion}',
    'resumen.estancias': 'Rooms',
    'resumen.videos': 'Videos',
    'resumen.fotos': 'Photos',
    'resumen.tamano': 'Size',
    'resumen.incidencias': 'Issues',
    'resumen.sello': 'Proof seal',
    'resumen.sello_gps': 'GPS {lat}, {lng} · ±{precision} m',
    'resumen.sello_sin_gps': 'No GPS: the phone did not give a position',
    'resumen.sello_dispositivo': '{dispositivo} · app {version}',
    'resumen.nota': 'Note for the office (optional)',
    'resumen.nota_ejemplo': 'e.g. the terrace was full of sand, it took longer',
    'resumen.finalizar': 'Finish and upload',
    'resumen.pendientes': 'You still have {n} room{n:|s} open',
    'resumen.pendientes_ayuda':
        'Close them or mark them as not accessible with a reason. You cannot finish with rooms open.',
    'resumen.subida_fondo': 'It will upload in the background even if you close the app',
    'resumen.subida_primer_plano': 'Keep the app open until the upload finishes',
    'resumen.subida_wifi': 'Waiting for WiFi to upload',

    // --- 08 · Upload queue ---------------------------------------------
    'cola.titulo': 'Upload queue',
    'cola.resumen': '{n} cleaning{n:|s} waiting · {tamano}',
    'cola.resumen_vacia': 'All uploaded. Nothing left on the phone.',
    'cola.subiendo': '{pct}% · {enviado}/{total}',
    'cola.reanudable': 'Resumable · 5 MB chunks',
    'cola.en_cola': 'Queued',
    'cola.fallida': 'Failed {n} time{n:|s}',
    'cola.reintentar_ahora': 'Retry now',
    'cola.esperando_red': 'Waiting for a connection',
    'cola.esperando_wifi': 'Waiting for WiFi',
    'cola.solo_wifi': 'Upload on WiFi only',
    'cola.almacenamiento': 'local storage {usado} / device limit {limite}',
    'cola.almacenamiento_aviso':
        'Your phone is running out of space. Get on WiFi to empty the queue before recording more.',
    'cola.pantalla_encendida': 'Keep the screen on while it uploads',

    // --- Network and session errors -------------------------------------
    'error.sin_red': 'Offline',
    'error.sin_red_ayuda':
        'You can carry on working: everything is saved on the phone and uploads when the network is back.',
    'error.dispositivo_revocado': 'This phone is no longer authorised',
    'error.inesperado': 'Something went wrong',
    'alta.rgpd_titulo': 'Data protection',
    'alta.rgpd_que':
        'We keep your name, the phone you link and, if you fill them in, your phone number and email. Your name goes in the filename of every video you record and the office can see it.',
    'alta.rgpd_plazo': 'Cleanings are kept for {dias} day{dias:|s}.',
    'alta.rgpd_plazo_indefinido': 'Cleanings are kept with no fixed time limit.',
    'alta.rgpd_anonimiza': 'When that runs out, who did them is deleted too.',
    'alta.rgpd_responsable': 'Data controller',
    'alta.rgpd_derechos':
        'You can ask to see your data, to have it corrected or to have it deleted by writing to {contacto}.',
    'alta.rgpd_sin_configurar':
        'This installation does not have the controller’s contact details set up yet. Ask the office for them.',
} as const;
